import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import RouteMap from "@/components/RouteMap";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, CheckSquare, Square, Loader } from "lucide-react";

const STATUSES = ["Draft", "Submitted", "Under Review", "In Planning", "Awaiting Client Confirmation", "Confirmed", "In Progress", "Completed", "Cancelled"];
const TASK_STATUSES = ["Not Started", "In Progress", "Waiting for Supplier", "Waiting for Client", "Completed", "Cancelled"];
const STATUS_PILL = {
  Submitted: "as-pill-blue", "Under Review": "as-pill-blue",
  "In Planning": "as-pill-orange", "Awaiting Client Confirmation": "as-pill-orange",
  Confirmed: "as-pill-gold", "In Progress": "as-pill-gold",
  Completed: "as-pill-green", Cancelled: "as-pill-red", Draft: "as-pill-muted",
};

export default function OpsTripDetail() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [note, setNote] = useState("");
  const [internal, setInternal] = useState(true);

  const loadAll = async () => {
    const [t, ta] = await Promise.all([
      api.get(`/trips/${id}`).then(r => r.data),
      api.get(`/trips/${id}/tasks`).then(r => r.data),
    ]);
    setTrip(t); setTasks(ta);
  };
  useEffect(() => { loadAll(); }, [id]);

  const changeStatus = async (newStatus) => {
    await api.patch(`/trips/${id}`, { status: newStatus });
    toast.success(`Status updated → ${newStatus}`);
    loadAll();
  };

  const changeTaskStatus = async (tid, status) => {
    await api.patch(`/tasks/${tid}`, { status });
    loadAll();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/trips/${id}/notes`, { message: note, internal });
    setNote("");
    toast.success("Note added.");
    loadAll();
  };

  if (!trip) return <DashboardLayout role="ops"><div className="p-10 text-[#756E64]">Loading…</div></DashboardLayout>;
  const leg = trip.legs?.[0] || {};

  return (
    <DashboardLayout role="ops">
      <div className="max-w-[1500px] mx-auto px-8 py-10">
        <Link to="/ops/trips" className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] hover:text-[#1A1D24] flex items-center gap-2" data-testid="ops-trip-back"><ArrowLeft size={13}/> Missions</Link>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <div className={`as-pill ${STATUS_PILL[trip.status]}`}>{trip.status}</div>
            <h1 className="as-serif text-6xl font-light mt-4 leading-none" data-testid="ops-trip-route">
              {leg.departure_airport} <span className="as-gold">→</span> {leg.arrival_airport}
            </h1>
            <div className="as-mono text-sm text-[#756E64] mt-3">{new Date(leg.departure_datetime).toUTCString()} · {trip.client_name}</div>

            {/* Status selector */}
            <div className="mt-6 flex items-center gap-3">
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Update status:</div>
              <select value={trip.status} onChange={e => changeStatus(e.target.value)} className="bg-[#FAF7F0] border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-3 py-2 rounded-sm text-sm" data-testid="ops-status-select">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            {/* Tasks */}
            <div className="as-card mt-8" data-testid="ops-task-checklist">
              <div className="p-5 border-b border-[#E5DFD2] flex items-center justify-between">
                <div className="as-overline">Aviation Service Checklist</div>
                <div className="as-mono text-[10px] text-[#756E64]">{tasks.filter(t=>t.status==='Completed').length} / {tasks.length} complete</div>
              </div>
              <ul className="divide-y divide-[#E5DFD2]">
                {tasks.map(t => {
                  const done = t.status === "Completed";
                  return (
                    <li key={t.id} className="p-4 flex items-center gap-4">
                      <button
                        onClick={() => changeTaskStatus(t.id, done ? "Not Started" : "Completed")}
                        className={done ? "as-gold" : "text-[#756E64]"}
                        data-testid={`task-toggle-${t.id}`}
                      >
                        {done ? <CheckSquare size={18}/> : t.status === "In Progress" ? <Loader size={18} className="animate-spin"/> : <Square size={18}/>}
                      </button>
                      <div className="flex-1">
                        <div className={`text-sm ${done ? "line-through text-[#756E64]" : "text-[#1A1D24]"}`}>{t.title}</div>
                        <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-1">{t.category} · {t.priority} · Due {t.due_date}</div>
                      </div>
                      <select value={t.status} onChange={e => changeTaskStatus(t.id, e.target.value)} className="bg-[#FAF7F0] border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-2 py-1 rounded-sm text-xs" data-testid={`task-status-${t.id}`}>
                        {TASK_STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </li>
                  );
                })}
                {tasks.length === 0 && <li className="p-6 text-center text-[#756E64] text-sm">No tasks yet.</li>}
              </ul>
            </div>

            {/* Notes */}
            <div className="as-card p-6 mt-6" data-testid="ops-notes">
              <div className="as-overline mb-4">Add Note</div>
              <textarea rows="3" value={note} onChange={e => setNote(e.target.value)} placeholder="Write a note…" className="w-full bg-[#FAF7F0] border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-4 py-3 rounded-sm text-[#1A1D24]" data-testid="note-textarea"/>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-sm text-[#756E64]">
                  <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} className="accent-[#A47E2A]" data-testid="note-internal"/>
                  Internal only
                </label>
                <button onClick={addNote} className="as-btn-gold px-5 py-2 rounded-sm as-mono text-[10px] uppercase tracking-widest" data-testid="add-note-btn">Add note</button>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="as-overline mb-3">Internal</div>
                  <ul className="space-y-3 text-sm">
                    {trip.internal_notes?.slice().reverse().map((n,i) => (
                      <li key={i} className="border-l-2 border-[#756E64] pl-3">
                        <div>{n.message}</div>
                        <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-1">{n.actor}</div>
                      </li>
                    ))}
                    {(!trip.internal_notes || trip.internal_notes.length === 0) && <li className="text-[#756E64] text-sm">No internal notes.</li>}
                  </ul>
                </div>
                <div>
                  <div className="as-overline mb-3">Client-visible</div>
                  <ul className="space-y-3 text-sm">
                    {trip.client_notes?.slice().reverse().map((n,i) => (
                      <li key={i} className="border-l-2 border-[#A47E2A] pl-3">
                        <div>{n.message}</div>
                        <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-1">{n.actor}</div>
                      </li>
                    ))}
                    {(!trip.client_notes || trip.client_notes.length === 0) && <li className="text-[#756E64] text-sm">None.</li>}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="md:col-span-4 space-y-6">
            <RouteMap
              departureCode={leg.departure_airport}
              arrivalCode={leg.arrival_airport}
              tail={trip.aircraft_tail}
            />

            <div className="as-card p-5">
              <div className="as-overline mb-3">Timeline</div>
              <ul className="space-y-3">
                {trip.timeline?.slice().reverse().map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="w-px bg-[#A47E2A] shrink-0"/>
                    <div>
                      <div className="text-xs text-[#1A1D24]">{t.message}</div>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-0.5">{new Date(t.at).toUTCString().slice(5,22)} · {t.actor}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="as-card p-5">
              <div className="as-overline mb-3">Route</div>
              <ul className="space-y-2 text-sm">
                {trip.legs?.map((l,i) => (
                  <li key={i} className="flex items-center gap-2">
                    <MapPin size={12} className="as-gold"/> {l.departure_airport} → {l.arrival_airport} <span className="as-mono text-[10px] text-[#756E64] ml-auto">{new Date(l.departure_datetime).toUTCString().slice(5,16)}</span>
                  </li>
                ))}
              </ul>
              <div className="as-divider my-4"/>
              <div className="text-xs text-[#756E64]">
                <div>Passengers: <span className="text-[#1A1D24]">{trip.passenger_count}</span></div>
                <div className="mt-1">Aircraft: <span className="text-[#1A1D24]">{trip.aircraft_preference}</span> {trip.aircraft_tail && <span className="as-mono as-gold ml-1">{trip.aircraft_tail}</span>}</div>
                <div className="mt-1">Owner: <span className="text-[#1A1D24]">{trip.owner_name || "Unassigned"}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
